import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DollarSign, TrendingUp, ShoppingCart, UserPlus, Building2, RefreshCw, Store,
  AlertTriangle, Plus, Map, Crosshair, FileText, Megaphone, BadgeCheck, Loader2, Receipt,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { KpiCard, Panel, money, OVERLINE, H1, btnSecondary, useAdmin } from "@/components/admin/ui";

const RANGES = [["today", "Today"], ["7d", "7 Days"], ["30d", "30 Days"], ["month", "This Month"], ["year", "This Year"], ["all", "All Time"]];

function Stat({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#E5E2DC] last:border-0">
      <span className="text-sm text-[#5C6B61]">{label}</span>
      <span className={`font-serif text-lg tabular-nums ${tone === "warn" && value > 0 ? "text-[#FF6A00]" : tone === "danger" && value > 0 ? "text-[#D9381E]" : "text-[#1F2A22]"}`}>{value}</span>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const [range, setRange] = useState("30d");
  const [d, setD] = useState(null);
  const finance = admin?.permissions?.includes("finance");

  useEffect(() => { setD(null); api.adminDashboard({ range }).then(setD).catch(() => {}); }, [range]);

  const quick = [
    { label: "Add Business", icon: Building2, to: "/admin/businesses", perm: "businesses" },
    { label: "Add Hunt", icon: Crosshair, to: "/admin/hunts", perm: "hunts" },
    { label: "Add Recipe", icon: FileText, to: "/admin/content", perm: "content" },
    { label: "Add State Partnership", icon: Map, to: "/admin/states", perm: "states" },
    { label: "Create Promotion", icon: Megaphone, to: "/admin/advertising", perm: "advertising" },
    { label: "Review Approvals", icon: BadgeCheck, to: "/admin/approvals", perm: "approvals" },
  ].filter((q) => admin?.permissions?.includes(q.perm));

  return (
    <div className="p-6 md:p-8" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className={OVERLINE}>HomeRange · Owner Dashboard</span>
          <h1 className={`${H1} mt-1`}>Company health at a glance</h1>
        </div>
        <div className="flex flex-wrap gap-1 border border-[#E5E2DC] bg-white p-1" data-testid="dashboard-range">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${range === k ? "bg-[#1F2A22] text-[#F9F8F6]" : "text-[#5C6B61] hover:bg-[#F2F0EB]"}`} data-testid={`range-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      {quick.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-6" data-testid="quick-actions">
          {quick.map((q) => (
            <button key={q.label} onClick={() => navigate(q.to)} className={btnSecondary} data-testid={`quick-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Plus size={14} /> {q.label}
            </button>
          ))}
        </div>
      )}

      {!d ? (
        <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
      ) : (
        <>
          {/* TODAY */}
          <div className="mt-8"><span className={OVERLINE}>Today</span></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
            {finance && <KpiCard label="Sales" value={money(d.today.sales_cents)} icon={DollarSign} testid="kpi-today-sales" />}
            {finance && <KpiCard label="HomeRange Revenue" value={money(d.today.homerange_revenue_cents)} icon={TrendingUp} accent testid="kpi-today-revenue" />}
            <KpiCard label="Orders" value={d.today.orders} icon={ShoppingCart} testid="kpi-today-orders" />
            <KpiCard label="New Users" value={d.today.new_users} icon={UserPlus} testid="kpi-today-users" />
          </div>

          {/* THIS MONTH */}
          <div className="mt-8"><span className={OVERLINE}>This Month</span></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
            {finance && <KpiCard label="Gross Marketplace Sales" value={money(d.month.gmv_cents)} icon={Store} testid="kpi-month-gmv" />}
            {finance && <KpiCard label="HomeRange Revenue" value={money(d.month.homerange_revenue_cents)} icon={TrendingUp} accent testid="kpi-month-revenue" />}
            {finance && <KpiCard label="Subscription Revenue" value={money(d.month.subscription_revenue_cents)} icon={RefreshCw} testid="kpi-month-subs" />}
            {finance && <KpiCard label="Marketplace Fee Revenue" value={money(d.month.marketplace_fee_revenue_cents)} icon={Receipt} testid="kpi-month-fees" />}
            <KpiCard label="New Businesses" value={d.month.new_businesses} icon={Building2} testid="kpi-month-biz" />
            <KpiCard label="New Users" value={d.month.new_users} icon={UserPlus} testid="kpi-month-users" />
          </div>

          {/* Trend chart */}
          {finance && d.trend && (
            <div className="mt-8">
              <Panel title="HomeRange revenue trend">
                {d.trend.length === 0 ? (
                  <div className="h-64 grid place-items-center text-sm text-[#5C6B61]">No revenue in this period yet.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={d.trend.map((t) => ({ date: t.date.slice(5), revenue: (t.revenue_cents || 0) / 100 }))}>
                      <defs>
                        <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#FF6A00" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#FF6A00" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#E5E2DC" strokeDasharray="4 4" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#5C6B61", fontSize: 11 }} axisLine={{ stroke: "#E5E2DC" }} tickLine={false} />
                      <YAxis tick={{ fill: "#5C6B61", fontSize: 11 }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `$${v}`} />
                      <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, "Revenue"]} contentStyle={{ border: "1px solid #E5E2DC", borderRadius: 0 }} />
                      <Area type="monotone" dataKey="revenue" stroke="#FF6A00" strokeWidth={2} fill="url(#rev)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </Panel>
            </div>
          )}

          {/* Health grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            <Panel title="Business Health">
              <Stat label="Active businesses" value={d.business_health.active_businesses} />
              <Stat label="Total businesses" value={d.business_health.total_businesses} />
              <Stat label="Pending applications" value={d.business_health.pending_applications} tone="warn" />
              <Stat label="Active subscriptions" value={d.business_health.active_subscriptions} />
              <Stat label="Past-due subscriptions" value={d.business_health.past_due_subscriptions} tone="warn" />
            </Panel>
            <Panel title="Marketplace Health">
              <Stat label="Active listings" value={d.marketplace_health.active_listings} />
              <Stat label="Orders (all time)" value={d.marketplace_health.orders} />
              <Stat label="Avg order value" value={money(d.marketplace_health.avg_order_value_cents)} />
              <Stat label="Conversion rate" value={d.marketplace_health.conversion_rate ?? "—"} />
            </Panel>
            <Panel title="Partnership Pipeline">
              <Stat label="Not contacted" value={d.partnership_pipeline.not_contacted} />
              <Stat label="States contacted" value={d.partnership_pipeline.states_contacted} />
              <Stat label="In discussion" value={d.partnership_pipeline.in_discussion} />
              <Stat label="Founding states" value={d.partnership_pipeline.founding_states} />
              <Stat label="Paid states" value={d.partnership_pipeline.paid_states} />
            </Panel>
            <Panel title="Needs Attention" className="border-[#FF6A00]/40">
              <Stat label="Business approvals" value={d.needs_attention.business_approvals} tone="warn" />
              <Stat label="Listings awaiting review" value={d.needs_attention.listings_pending} tone="warn" />
              <Stat label="Failed payments" value={d.needs_attention.failed_payments} tone="danger" />
              <Stat label="Disputes" value={d.needs_attention.disputes} tone="danger" />
              <Stat label="Support requests" value={d.needs_attention.support_requests} tone="warn" />
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
